import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { Box, IconButton, Alert } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ErrorIcon from '@mui/icons-material/Error';

// Simple AudioManager for managing multiple audio instances
class AudioManager {
  constructor() {
    this.currentAudio = null;
    this.currentSetPlaying = null;
    this.audioInstances = new Set();
  }

  register(audio, setPlaying) {
    this.audioInstances.add({ audio, setPlaying });
    
    audio.addEventListener('play', () => {
      // Stop all other audio instances when this one plays
      this.audioInstances.forEach(({ audio: otherAudio, setPlaying: otherSetPlaying }) => {
        if (otherAudio !== audio && !otherAudio.paused) {
          otherAudio.pause();
          otherAudio.currentTime = 0;
          if (otherSetPlaying) {
            otherSetPlaying(false);
          }
        }
      });
      
      this.currentAudio = audio;
      this.currentSetPlaying = setPlaying;
    });

    audio.addEventListener('pause', () => {
      if (setPlaying) setPlaying(false);
    });

    audio.addEventListener('ended', () => {
      if (setPlaying) setPlaying(false);
      this.currentAudio = null;
      this.currentSetPlaying = null;
    });
  }

  play(audio, setPlaying) {
    // Stop all other audio instances
    this.audioInstances.forEach(({ audio: otherAudio, setPlaying: otherSetPlaying }) => {
      if (otherAudio !== audio && !otherAudio.paused) {
        otherAudio.pause();
        otherAudio.currentTime = 0;
        if (otherSetPlaying) {
          otherSetPlaying(false);
        }
      }
    });
    
    this.currentAudio = audio;
    this.currentSetPlaying = setPlaying;
  }

  pauseAll() {
    this.audioInstances.forEach(({ audio, setPlaying }) => {
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0; // Reset to beginning
        setPlaying(false);
      }
    });
    this.currentAudio = null;
    this.currentSetPlaying = null;
  }

  stopAll() {
    this.audioInstances.forEach(({ audio, setPlaying }) => {
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0; // Reset to beginning
        setPlaying(false);
      }
    });
    this.currentAudio = null;
    this.currentSetPlaying = null;
  }

  stop() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      if (this.currentSetPlaying) {
        this.currentSetPlaying(false);
      }
      this.currentAudio = null;
      this.currentSetPlaying = null;
    }
  }

  unregister(audio) {
    this.audioInstances = new Set([...this.audioInstances].filter(instance => instance.audio !== audio));
  }
}

// Global audio manager instance
const audioManager = new AudioManager();

// Backend audio session management
const notifyBackendAudioPlay = async (userId, audioUrl, sessionId = 'default') => {
  try {
    const token = localStorage.getItem('apiToken') || process.env.REACT_APP_API_TOKEN;
    await fetch(`${process.env.REACT_APP_API_URL}/api/audio/play`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        user_id: userId,
        audio_url: audioUrl,
        session_id: sessionId
      })
    });
  } catch (error) {
    console.error('Failed to notify backend of audio play:', error);
  }
};

const notifyBackendAudioStop = async (userId, sessionId = 'default') => {
  try {
    const token = localStorage.getItem('apiToken') || process.env.REACT_APP_API_TOKEN;
    await fetch(`${process.env.REACT_APP_API_URL}/api/audio/stop`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        user_id: userId,
        session_id: sessionId
      })
    });
  } catch (error) {
    console.error('Failed to notify backend of audio stop:', error);
  }
};

const AudioPlayer = forwardRef(({ src, autoPlay = false, userId = 'default' }, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  // Construct full URL if src is relative, with robust base fallback
  const baseUrl = (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) || (typeof window !== 'undefined' ? window.location.origin : '');
  const fullAudioUrl = src && src.startsWith('/') ? `${baseUrl}${src}` : src;
  const isCrossOrigin = (() => {
    try {
      if (!fullAudioUrl || !fullAudioUrl.startsWith('http')) return false;
      const u = new URL(fullAudioUrl);
      return typeof window !== 'undefined' && u.origin !== window.location.origin;
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !fullAudioUrl) return;

    setIsLoading(true);
    setHasError(false);

    // Audio access testing disabled to prevent console errors
    // The audio files are accessible when needed by the audio element itself

    // Set up audio event listeners
    const handleLoadedMetadata = () => {
      console.log('🎵 Audio metadata loaded:', {
        duration: audio.duration,
        readyState: audio.readyState,
        src: fullAudioUrl
      });

      
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
      
      setIsLoading(false);
      setHasError(false);
      
      // Register with audio manager
      audioManager.register(audio, setIsPlaying);
      
      // Handle autoplay
      if (autoPlay) {
        audioManager.play(audio, setIsPlaying);
        setIsPlaying(true);
        // Notify backend that audio started playing
        notifyBackendAudioPlay(userId, fullAudioUrl).catch(console.error);
        audio.play().catch((error) => {
          console.error('Autoplay failed:', error);
          setIsPlaying(false);
        });
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleError = (e) => {
      const mediaError = audio.error;
      const details = {
        currentSrc: audio.currentSrc,
        networkState: audio.networkState,
        readyState: audio.readyState,
        errorCode: mediaError ? mediaError.code : null
      };
      console.error('Audio loading error:', e);
      console.error('Audio error details:', details);

      // If this is a blob URL, try a single safe reload to recover
      const isBlobUrl = fullAudioUrl && fullAudioUrl.startsWith('blob:');
      if (isBlobUrl) {
        try {
          // Reset and reload once to handle transient decode/network issues
          audio.pause();
          const originalSrc = fullAudioUrl;
          audio.src = '';
          // Reassign the original src and force load
          audio.src = originalSrc;
          audio.load();
          // If canplay fires after retry, clear the error state
          const onceCanPlay = () => {
            audio.removeEventListener('canplay', onceCanPlay);
            setHasError(false);
            setIsLoading(false);
          };
          audio.addEventListener('canplay', onceCanPlay);
        } catch (reloadErr) {
          console.warn('Blob reload failed:', reloadErr);
        }
      }

      setHasError(true);
      setIsLoading(false);
      setDuration(0); // Set duration to 0 when there's an error

      // Single non-blob retry: useful for transient CORS/CDN hiccups
      if (!isBlobUrl) {
        try {
          const onceRetry = () => {
            audio.removeEventListener('canplay', onceRetry);
            setHasError(false);
            setIsLoading(false);
          };
          audio.addEventListener('canplay', onceRetry, { once: true });
          audio.pause();
          const originalSrc = fullAudioUrl;
          audio.src = '';
          audio.src = originalSrc;
          audio.load();
        } catch (reloadErr) {
          console.warn('Reload failed:', reloadErr);
        }
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      
      // Try to get duration if not already set (for mp3 files)
      if ((!duration || duration === 0) && audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    const handleDurationChange = () => {
      console.log('🎵 Duration changed:', audio.duration);
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    const handleLoadedData = () => {
      console.log('🎵 Audio data loaded:', {
        duration: audio.duration,
        readyState: audio.readyState
      });
      
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    const handleProgress = () => {
      // For MP3 files, sometimes duration becomes available during buffering
      if ((!duration || duration === 0) && audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        console.log('🎵 Duration found during progress:', audio.duration);
        setDuration(audio.duration);
      }
    };

    const handleSeeked = () => {
      // Sometimes seeking triggers duration detection for MP3 files
      if ((!duration || duration === 0) && audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        console.log('🎵 Duration found after seeking:', audio.duration);
        setDuration(audio.duration);
      }
    };

    const handleLoadStart = () => {
      console.log('🎵 Audio load started');
      // Don't reset duration to prevent --:-- display on reload
      // setDuration(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('progress', handleProgress);
    audio.addEventListener('seeked', handleSeeked);
    audio.addEventListener('loadstart', handleLoadStart);

    // Set the audio source with MP3 support for better Safari compatibility
    audio.src = fullAudioUrl;
    
    // More aggressive preloading for user audio to prevent double-click issue
    // User audio should be ready to play immediately when clicked
    const isBlobUrl = fullAudioUrl && fullAudioUrl.startsWith('blob:');
    const isUserAudio = fullAudioUrl && (fullAudioUrl.includes('/static/audio/') || isBlobUrl);
    
    if (isUserAudio && !isBlobUrl) {
      // For persistent user audio (server URL), preload more aggressively
      audio.preload = 'auto'; // Load the entire audio file
    } else {
      // For assistant audio, use metadata preloading to save bandwidth
      audio.preload = 'metadata';
    }
    
    audio.load();
    
    // For user audio, try to ensure it's fully loaded before user interaction
    if (isUserAudio && !isBlobUrl) {
      // Force loading to start immediately
      const forceLoad = () => {
        if (audio.readyState < 2) {
          audio.currentTime = 0.01;
          setTimeout(() => {
            audio.currentTime = 0;
          }, 100);
        }
      };
      
      // Try to force load after a short delay
      setTimeout(forceLoad, 500);
    }
    
    // Immediate attempt to get duration if metadata is already available
    // This helps when audio files are cached from previous sessions
    if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
      console.log('🎵 Duration immediately available:', audio.duration);
      setDuration(audio.duration);
      setIsLoading(false);
    }
    
    // Additional fallback for metadata loading after browser reload
    // Some browsers may not automatically load metadata after page reload
    // This timeout ensures we try to force metadata loading if it hasn't happened
    const metadataFallbackTimeout = setTimeout(() => {
      if ((!duration || duration === 0) && audio.readyState < 1) {
        console.log('🎵 Forcing metadata reload after timeout');
        // Try to force metadata loading by briefly setting currentTime
        const originalTime = audio.currentTime;
        audio.currentTime = 0.01;
        setTimeout(() => {
          audio.currentTime = originalTime;
        }, 100);
      }
    }, 2000); // Wait 2 seconds before trying fallback
    
    return () => {
      clearTimeout(metadataFallbackTimeout);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('progress', handleProgress);
      audio.removeEventListener('seeked', handleSeeked);
      audio.removeEventListener('loadstart', handleLoadStart);
      
      // Clean up any audio-specific resources
      // MP3 files don't require special cleanup
      
      audioManager.unregister(audio);
    };
  }, [fullAudioUrl, autoPlay, duration]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    if (isPlaying) {
      // Stop audio completely (pause and reset to beginning)
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      // Notify backend that audio stopped
      await notifyBackendAudioStop(userId);
    } else {
      // Ensure audio is ready to play before attempting to play
      if (audio.readyState < 2) {
        // If metadata isn't loaded yet, wait for it
        setIsLoading(true);
        
        const handleCanPlayThrough = () => {
          audio.removeEventListener('canplaythrough', handleCanPlayThrough);
          audio.removeEventListener('loadeddata', handleCanPlayThrough);
          setIsLoading(false);
          
          // Now try to play
          audioManager.play(audio, setIsPlaying);
          setIsPlaying(true);
          notifyBackendAudioPlay(userId, fullAudioUrl);
          audio.play().catch((error) => {
            console.error('Play failed:', error);
            setHasError(true);
            setIsPlaying(false);
          });
        };
        
        // Listen for when audio is ready to play
        audio.addEventListener('canplaythrough', handleCanPlayThrough);
        audio.addEventListener('loadeddata', handleCanPlayThrough);
        
        // Force load if not already loading
        if (audio.readyState === 0) {
          audio.load();
        }
        
        // Fallback timeout in case events don't fire
        setTimeout(() => {
          if (audio.readyState >= 2) {
            handleCanPlayThrough();
          }
        }, 1000);
      } else {
        // Audio is ready, play immediately
        audioManager.play(audio, setIsPlaying);
        setIsPlaying(true);
        // Notify backend that audio started playing
        await notifyBackendAudioPlay(userId, fullAudioUrl);
        audio.play().catch((error) => {
          console.error('Play failed:', error);
          setHasError(true);
          setIsPlaying(false);
        });
      }
    }
  };

  const formatDuration = (time) => {
    // Show 'N/A' when there's an error loading the audio
    if (hasError) {
      return 'N/A';
    }
    
    if (!time || isNaN(time) || !isFinite(time) || time <= 0) {      
      // Show loading indicator when duration is not available, regardless of isLoading state
      // This prevents --:-- from showing on reload before duration is detected
      return '...';
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getErrorMessage = () => {
    const isMp3File = fullAudioUrl && (fullAudioUrl.includes('.mp3') || fullAudioUrl.includes('audio/mp3'));
    const isLocalhost = fullAudioUrl && fullAudioUrl.includes('localhost');
    const isBlobUrl = fullAudioUrl && fullAudioUrl.startsWith('blob:');
    
    if (isBlobUrl) {
      return 'Temporary audio file expired. Please try recording again.';
    } else if (isMp3File && isLocalhost) {
      return 'Unable to load audio file. Please check your internet connection and try again.';
    } else if (isLocalhost) {
      return 'Unable to load audio file. Please check your internet connection and try again.';
    } else {
      return 'Audio playback error. Please try refreshing the page or contact support.';
    }
  };

  if (hasError) {
    const errorMessage = getErrorMessage();
    
    // Log additional debugging info
    console.error('Audio error details:', {
      src: fullAudioUrl,
      userAgent: navigator.userAgent,
      online: navigator.onLine
    });
      
    return (
      <Alert 
        severity="error" 
        icon={<ErrorIcon />}
        sx={{ 
          width: '100%',
          borderRadius: 4,
          background: 'linear-gradient(145deg, #ffebee 0%, #fce4ec 100%)',
          border: '1px solid rgba(244, 67, 54, 0.2)',
          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.1)',
          '& .MuiAlert-icon': {
            color: '#d32f2f'
          },
          '& .MuiAlert-message': {
            color: '#c62828',
            fontWeight: 500
          }
        }}
      >
        {errorMessage}
      </Alert>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      width: '100%',
      gap: { xs: 0.5, sm: 1 },
      minWidth: { xs: 140, sm: 180 }
    }}>
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        preload="metadata" 
        crossOrigin={isCrossOrigin ? 'anonymous' : undefined}
        onLoadStart={() => console.log('🎵 Audio load started')}
        onSuspend={() => console.log('🎵 Audio loading suspended')}
        onStalled={() => console.log('🎵 Audio loading stalled')}
        onCanPlay={() => console.log('🎵 Audio can play')}
        onLoadedMetadata={() => console.log('🎵 Audio metadata loaded (HTML event)')}
      />
      
      <IconButton 
        onClick={togglePlayPause} 
        disabled={isLoading || hasError}
        size="small"
        className="audio-button"
        title={isLoading ? 'Loading audio...' : isPlaying ? 'Pause' : 'Play'}
        sx={{ 
          color: '#2196f3',
          width: 32, 
          height: 32,
          background: 'transparent',
          border: '1px solid #2196f3',
          borderRadius: '50%',
          '&:hover': {
            background: '#2196f3',
            color: 'white'
          },
          '&:disabled': {
            color: 'rgba(33, 150, 243, 0.3)',
            borderColor: 'rgba(33, 150, 243, 0.3)',
            background: 'transparent'
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1rem'
          }
        }}
      >
        {isLoading ? (
          <Box sx={{ 
            width: 16, 
            height: 16, 
            border: '2px solid #e3f2fd', 
            borderTop: '2px solid #1976d2', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite'
          }} />
        ) : isPlaying ? (
          <PauseIcon fontSize="small" />
        ) : (
          <PlayArrowIcon fontSize="small" />
        )}
      </IconButton>
      
      {/* Progress bar */}
      <Box sx={{ 
        flex: 1,
        height: { xs: 2, sm: 3 },
        background: '#e0e0e0',
        borderRadius: 1.5,
        overflow: 'hidden',
        minWidth: { xs: 60, sm: 80 }
      }}>
        <Box sx={{
          height: '100%',
          width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
          background: '#2196f3',
          borderRadius: 1.5,
          transition: 'width 0.2s ease'
        }} />
      </Box>
      
      <Box sx={{ 
        fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
        color: '#666', 
        minWidth: { xs: 35, sm: 40 }, 
        textAlign: 'right',
        fontWeight: 400,
        whiteSpace: 'nowrap',
        overflow: 'hidden'
      }}>
        {formatDuration(duration)}
      </Box>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
});

// Export the audio manager for use in other components
export { audioManager };
export default AudioPlayer;